// Friendly per-model copy for the chat/Settings model pickers (owner
// feedback round 2, screenshot 4): a short "what is this model like" tagline
// plus a rough per-answer cost estimate, both rendered as MultiSelectOption's
// `note` line. Taglines and the estimate wording live in bot.json
// (composer.tagline.<modelId> / composer.estimatePerAnswer /
// composer.estimateBelowCent) — this is UI chrome shown in the picker, not
// catalog data, so it follows the active locale like everything else in the
// 'bot' namespace. This file only does the math and the locale-aware number
// formatting.

import type { BotModelPrice } from './api/botApi'

/** Minimal structural shape of react-i18next's `t` needed for the
 *  `returnObjects` call below — mirrors botUtils.ts's `Translator` but with
 *  a widened return type. */
type ObjectTranslator = (key: string, options?: Record<string, unknown>) => unknown

/**
 * Tagline for one model, pulled from bot.json's `composer.tagline` map.
 * Fetches the whole map in one `t()` call (`returnObjects: true`) and
 * indexes it with plain JS property access — NOT a second `t()` call keyed
 * on the model id — because ids like "gpt-5.6-sol" contain literal dots
 * that i18next's default key separator would otherwise read as nested path
 * segments, breaking the lookup.
 */
export function getModelTagline(t: ObjectTranslator, modelId: string): string {
  const taglines = t('composer.tagline', { returnObjects: true }) as Record<string, string>
  return taglines[modelId] ?? ''
}

// Rough per-answer shape (Design Spec's own ballpark, not a hard bound): most
// questions read a modest amount of schema/context and get back a short
// prose answer — 3000 in / 500 out is close enough for a "≈" estimate, not
// an invoice.
const EST_INPUT_TOKENS = 3000
const EST_OUTPUT_TOKENS = 500

/** null only when `price` itself is null (model has no catalog pricing yet) — callers omit the estimate in that case rather than showing "≈ 0,00 €". */
export function estimateAnswerEur(price: BotModelPrice | null, usdEurRate = 0.92): number | null {
  if (!price) return null
  const usd = (EST_INPUT_TOKENS * price.inPerMtok + EST_OUTPUT_TOKENS * price.outPerMtok) / 1_000_000
  return usd * usdEurRate
}

/** Minimal structural shape of react-i18next's `t` — mirrors botUtils.ts's `Translator`. */
type Translator = (key: string, options?: Record<string, unknown>) => string

/**
 * 2 decimals, formatted via the active i18next `language` (not a hardcoded
 * locale) so an English-locale user reads "1.23 €" not "1,23 €"; sub-cent
 * estimates collapse to the translated `composer.estimateBelowCent` line
 * rather than misleadingly precise zeros.
 */
export function formatEstimate(eur: number, t: Translator, language: string): string {
  if (eur < 0.01) return t('composer.estimateBelowCent')
  const amount = eur.toLocaleString(language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return t('composer.estimatePerAnswer', { amount })
}
